import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';
import { Megaphone, Pause, Play, BarChart3, Users, X } from 'lucide-react';
import ButtonPrimary from '../../components/button-refactor/button-primary';
import {
  AdCampaign,
  createAdCampaign,
  getMyAdCampaigns,
  setAdCampaignPaused,
  cancelAdCampaign,
  CreateAdCampaignPayload,
  AD_OBJECTIVES,
  estimateReach,
  ReachEstimate,
} from '../../api/ads/ads.api';
import { placementsForDistribution } from '../../api/ads/ad-placements';
import CreatorDistributionSection, { PickedCreator } from '../../components/ads/creator-distribution-section';
import AdStatsModal from '../../components/ads/ad-stats-modal';
import AdPreview from '../../components/ads/ad-preview';
import BusinessAccountSection from '../../components/ads/business-account-section';
import { splitOrdererAccounts } from '../../utils/individual-order';
import { AdAccount, getMyAdAccounts } from '../../api/ads/ad-account.api';
import AdCreativeForm from '../../components/ads/ad-creative-form';
import { getUserBalanceBuckets } from '../../api/payment/store.api';
import { VN_PROVINCES } from '../../constants/provinces';
import { useConsentGate } from '../../hooks/use-consent-gate';
import { routePaths } from '../../routes/routes-path';

const FORMATS = [
  { value: 'Native', label: 'Native (xen trong feed)' },
  { value: 'Banner', label: 'Banner (ảnh)' },
  { value: 'Video', label: 'Video' },
];
const PRICING = [
  { value: 'CPC', label: 'CPC — trả tiền mỗi click' },
  { value: 'CPM', label: 'CPM — trả tiền mỗi 1.000 hiển thị' },
];
const GENDERS = [
  { value: '', label: 'Mọi giới tính' },
  { value: 'Male', label: 'Nam' },
  { value: 'Female', label: 'Nữ' },
];
// Sở thích/mối quan tâm để "đánh đúng tệp" — quảng cáo chỉ hiện với người quan tâm chủ đề này.
const INTERESTS = ['Công nghệ', 'Game', 'Thời trang', 'Làm đẹp', 'Ẩm thực', 'Du lịch', 'Thể thao', 'Xe cộ', 'Giáo dục', 'Tài chính', 'Mẹ & bé', 'Giải trí', 'Sức khỏe', 'Bất động sản'];
// Vị trí hiển thị quảng cáo trong hệ sinh thái Cab.
const DEVICES = [
  { value: '', label: 'Mọi thiết bị' },
  { value: 'iOS', label: 'iOS (iPhone/iPad)' },
  { value: 'Android', label: 'Android' },
  { value: 'Web', label: 'Web (máy tính)' },
];

const STATUS_VI: Record<string, { label: string; cls: string }> = {
  Pending: { label: 'Chờ duyệt', cls: 'bg-amber-100 text-amber-700' },
  Active: { label: 'Đang chạy', cls: 'bg-emerald-100 text-emerald-700' },
  Paused: { label: 'Tạm dừng', cls: 'bg-slate-200 text-slate-700' },
  Rejected: { label: 'Bị từ chối', cls: 'bg-rose-100 text-rose-700' },
  Completed: { label: 'Hết ngân sách', cls: 'bg-blue-100 text-blue-700' },
  Cancelled: { label: 'Đã dừng', cls: 'bg-slate-200 text-slate-700' },
};

const emptyForm: CreateAdCampaignPayload = {
  adAccountId: null,
  name: '',
  objective: 'Awareness',
  format: 'Native',
  pricingModel: 'CPC',
  bidCa: 100,
  totalBudgetCa: 5000,
  dailyBudgetCa: null,
  targetGender: null,
  targetAgeMin: null,
  targetAgeMax: null,
  targetDevice: null,
  targetCity: null,
  targetInterests: [],
  targetPlacements: [],
  creatorDistribution: 'Auto',
  targetCreatorIds: [],
  landingUrl: '',
  title: '',
  body: '',
  imageUrl: '',
  videoUrl: '',
  ctaText: '',
};

const AdsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<AdCampaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<CreateAdCampaignPayload>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [pickedCreators, setPickedCreators] = useState<PickedCreator[]>([]);
  const [statsCampaign, setStatsCampaign] = useState<AdCampaign | null>(null);
  const [reach, setReach] = useState<ReachEstimate | null>(null);
  const [topUpCa, setTopUpCa] = useState<number | null>(null); // Ca nạp khả dụng để chạy ads
  const [bizAccounts, setBizAccounts] = useState<AdAccount[]>([]); // tài khoản DN đã xác thực (nguồn ví VND)
  const creditGate = useConsentGate('ads-credit'); // đồng ý điều khoản tín dụng khi chiến dịch DN tiêu vượt số dư
  const personalGate = useConsentGate('ads-personal'); // cá nhân chạy quảng cáo/đẩy bài → đồng ý điều khoản QC cá nhân
  const location = useLocation();

  // Video gửi sang từ Cab Studio → prefill vào creative (format Video). State mất khi refresh (đi thẳng từ Studio là ok).
  useEffect(() => {
    const url = (location.state as { studioVideoUrl?: string } | null)?.studioVideoUrl;
    if (url) {
      setForm((f) => ({ ...f, videoUrl: url, format: 'Video' }));
      toast.success('Đã đính kèm video từ Studio — điền ngân sách & nhắm mục tiêu rồi tạo chiến dịch.');
      window.history.replaceState({}, ''); // xoá state để F5 không đính lại
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setCampaigns(await getMyAdCampaigns());
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };
  // Tài khoản cá nhân KHÔNG là nguồn ngân sách Cab Ads doanh nghiệp (BE từ chối) nên tách riêng.
  const loadAccounts = () =>
    getMyAdAccounts()
      .then((accs) => {
        // Tài khoản cá nhân dùng để thuê creator (trang Hợp tác quảng cáo), không phải nguồn ngân sách Cab Ads.
        setBizAccounts(splitOrdererAccounts(accs).business);
      })
      .catch(() => undefined);

  useEffect(() => {
    load();
    getUserBalanceBuckets()
      .then((b) => setTopUpCa(b.topUpBalance))
      .catch(() => undefined);
    loadAccounts();
  }, []);

  // Nguồn ngân sách đang chọn (undefined = Ca cá nhân). Khả dụng để check trước khi tạo.
  // Với ví DN: khả dụng = số dư + hạn mức tín dụng (E5).
  const fundingAcc = bizAccounts.find((a) => a.id === form.adAccountId);
  const availableBudget = fundingAcc
    ? (fundingAcc.balanceVnd ?? 0) + (fundingAcc.creditLimitVnd ?? 0)
    : topUpCa ?? 0;

  const set = (patch: Partial<CreateAdCampaignPayload>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  // Đổi mục tiêu → gợi ý sẵn CTA phù hợp (nếu user chưa tự nhập).
  const onObjective = (value: string) => {
    const obj = AD_OBJECTIVES.find((o) => o.value === value);
    setForm((prev) => ({
      ...prev,
      objective: value,
      ctaText: !prev.ctaText || AD_OBJECTIVES.some((o) => o.cta === prev.ctaText)
        ? obj?.cta || prev.ctaText
        : prev.ctaText,
    }));
  };

  // Ước tính tiếp cận — gọi lại (debounce) mỗi khi đổi nhắm mục tiêu.
  const targetKey = `${form.targetGender}|${form.targetAgeMin}|${form.targetAgeMax}|${form.targetDevice}|${form.targetCity}`;
  useEffect(() => {
    const t = setTimeout(() => {
      estimateReach({
        targetGender: form.targetGender,
        targetAgeMin: form.targetAgeMin,
        targetAgeMax: form.targetAgeMax,
        targetDevice: form.targetDevice,
        targetCity: form.targetCity,
      })
        .then(setReach)
        .catch(() => undefined);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey]);

  const objectiveLabel = useMemo(
    () => AD_OBJECTIVES.find((o) => o.value === form.objective)?.label || form.objective,
    [form.objective]
  );

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Nhập tên chiến dịch.');
    if (!form.landingUrl.trim()) return toast.error('Nhập link đích (khi bấm quảng cáo).');
    if (form.creatorDistribution === 'Named' && (pickedCreators.length < 1 || pickedCreators.length > 20))
      return toast.error('Chọn từ 1 đến 20 creator.');
    if (form.bidCa <= 0 || form.totalBudgetCa <= 0)
      return toast.error('Giá thầu và ngân sách phải > 0.');
    if (form.totalBudgetCa > availableBudget)
      return toast.error(
        fundingAcc
          ? 'Ví doanh nghiệp không đủ số dư. Vui lòng nạp thêm.'
          : 'Không đủ Ca nạp. Vui lòng nạp thêm.'
      );
    // Nếu chiến dịch DN tiêu vượt số dư (dùng tín dụng) → bấm đồng ý Điều khoản Tín dụng trước.
    const usesCredit = !!fundingAcc && form.totalBudgetCa > (fundingAcc.balanceVnd ?? 0);
    if (usesCredit) creditGate.run(doCreate);
    else if (!fundingAcc) personalGate.run(doCreate); // cá nhân → đồng ý Điều khoản Quảng cáo/Đẩy bài trước
    else doCreate();
  };

  const doCreate = async () => {
    try {
      setSubmitting(true);
      await createAdCampaign(form);
      toast.success('Đã tạo chiến dịch! Chờ admin duyệt để bắt đầu chạy.');
      setPickedCreators([]);
      setForm({ ...emptyForm, adAccountId: form.adAccountId }); // giữ nguyên nguồn ngân sách đang chọn
      load();
      loadAccounts();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Tạo chiến dịch thất bại.');
    } finally {
      setSubmitting(false);
    }
  };

  const togglePause = async (c: AdCampaign) => {
    try {
      await setAdCampaignPaused(c.id, c.status !== 'Paused');
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Không đổi được trạng thái.');
    }
  };

  const cancelCampaign = async (c: AdCampaign) => {
    if (!window.confirm(`Dừng hẳn chiến dịch "${c.name}"? Phần ngân sách chưa tiêu (${(c.remainingCa || 0).toLocaleString('vi-VN')} Ca) sẽ được hoàn về ví.`)) return;
    try {
      await cancelAdCampaign(c.id);
      toast.success('Đã dừng chiến dịch và hoàn phần ngân sách chưa tiêu về ví.');
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Không dừng được chiến dịch.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      <Helmet>
        <title>Quảng cáo — Cab Ads</title>
      </Helmet>

      <div className="flex items-center gap-2">
        <Megaphone className="text-[#0071A6]" />
        <h1 className="text-2xl font-bold ui-text">Quảng cáo (Cab Ads)</h1>
      </div>
      <p className="text-sm ui-muted -mt-3">
        Chạy quảng cáo trên Cab.vn. Ngân sách trừ từ <b>Ca nạp</b> (Ca mua bằng tiền qua VNPay) — không dùng Ca đào.
        Tính phí theo click (CPC) hoặc mỗi 1.000 hiển thị (CPM).
      </p>
      {/* Thuê creator là sản phẩm khác, có trang riêng — sếp góp ý 14/10. */}
      <p className="text-sm ui-muted -mt-3">
        Muốn thuê creator đăng bài cho bạn?{' '}
        <Link to={routePaths.brandOrderer} className="font-semibold text-[#0057FF] hover:underline dark:text-blue-300">
          Sang trang Thuê creator
        </Link>
      </p>

      {topUpCa !== null && (
        <div className="flex items-center justify-between rounded-xl border border-[#48afff]/40 bg-[#f6fbff] dark:bg-slate-800 dark:border-slate-700 px-4 py-3 -mt-2">
          <span className="text-sm ui-muted">💳 Ca nạp khả dụng để chạy quảng cáo</span>
          <span className="text-lg font-bold text-[#0071A6] dark:text-[#48afff]">
            {topUpCa.toLocaleString('vi-VN')} Ca
          </span>
        </div>
      )}

      {/* Cab Ads Manager — tài khoản doanh nghiệp (E1) */}
      <BusinessAccountSection />

      {/* Form tạo chiến dịch */}
      <div className="rounded-xl ui-divider border p-4 space-y-3">
        <h2 className="font-semibold ui-text">Tạo chiến dịch mới</h2>

        {/* Nguồn ngân sách: Ca cá nhân hoặc ví VND của tài khoản DN đã xác thực (E3) */}
        {bizAccounts.length > 0 && (
          <div>
            <div className="text-sm ui-text mb-1">Nguồn ngân sách</div>
            <select
              className="w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none ui-text"
              value={form.adAccountId || ''}
              onChange={(e) => set({ adAccountId: e.target.value || null })}
            >
              <option value="">
                Ca cá nhân — {(topUpCa ?? 0).toLocaleString('vi-VN')} Ca khả dụng
              </option>
              {bizAccounts.map((a) => {
                const avail = (a.balanceVnd ?? 0) + (a.creditLimitVnd ?? 0);
                return (
                  <option key={a.id} value={a.id}>
                    {a.businessName} (ví DN) — khả dụng {avail.toLocaleString('vi-VN')} đ
                    {(a.creditLimitVnd ?? 0) > 0
                      ? ` (gồm hạn mức ${(a.creditLimitVnd ?? 0).toLocaleString('vi-VN')} đ)`
                      : ''}
                  </option>
                );
              })}
            </select>
            {fundingAcc ? (
              <p className="text-[11px] ui-muted mt-1">
                Ngân sách trừ từ ví VND của {fundingAcc.businessName} (số dư{' '}
                {(fundingAcc.balanceVnd ?? 0).toLocaleString('vi-VN')} đ
                {(fundingAcc.creditLimitVnd ?? 0) > 0
                  ? ` + hạn mức tín dụng ${(fundingAcc.creditLimitVnd ?? 0).toLocaleString('vi-VN')} đ`
                  : ''}
                ). Xuất hoá đơn VAT theo tài khoản DN.
                {(fundingAcc.balanceVnd ?? 0) < 0
                  ? ` Đang nợ ${Math.abs(fundingAcc.balanceVnd ?? 0).toLocaleString('vi-VN')} đ — nạp tiền để trả.`
                  : ''}
              </p>
            ) : (
              <p className="text-[11px] ui-muted mt-1">Ngân sách trừ từ Ca nạp cá nhân của bạn.</p>
            )}
          </div>
        )}

        {/* Mục tiêu chiến dịch */}
        <div>
          <div className="text-sm ui-text mb-1">Mục tiêu chiến dịch</div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {AD_OBJECTIVES.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => onObjective(o.value)}
                className={`rounded-lg border px-2 py-2 text-xs font-medium text-center transition-colors ${
                  form.objective === o.value
                    ? 'border-[#0071A6] bg-[#0071A6]/10 text-[#0071A6]'
                    : 'ui-divider ui-muted hover:bg-black/5 dark:hover:bg-white/5'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm ui-text">
            Tên chiến dịch
            <input
              className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none"
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="VD: Ra mắt sản phẩm X"
            />
          </label>
          <label className="text-sm ui-text">
            Link đích (khi bấm quảng cáo)
            <input
              className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none"
              value={form.landingUrl}
              onChange={(e) => set({ landingUrl: e.target.value })}
              placeholder="https://..."
            />
          </label>
          <label className="text-sm ui-text">
            Định dạng
            <select
              className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none"
              value={form.format}
              onChange={(e) => set({ format: e.target.value })}
            >
              {FORMATS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm ui-text">
            Cách tính phí
            <select
              className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none"
              value={form.pricingModel}
              onChange={(e) => set({ pricingModel: e.target.value })}
            >
              {PRICING.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm ui-text">
            Giá thầu (Ca) {form.pricingModel === 'CPC' ? '/ click' : '/ 1.000 hiển thị'}
            <input
              type="number"
              className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none"
              value={form.bidCa}
              onChange={(e) => set({ bidCa: Number(e.target.value) })}
            />
          </label>
          <label className="text-sm ui-text">
            Tổng ngân sách (Ca)
            <input
              type="number"
              className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none"
              value={form.totalBudgetCa}
              onChange={(e) => set({ totalBudgetCa: Number(e.target.value) })}
            />
          </label>
          <label className="text-sm ui-text">
            Ngân sách/ngày (Ca) — để trống nếu không giới hạn
            <input
              type="number"
              className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none"
              value={form.dailyBudgetCa ?? ''}
              onChange={(e) =>
                set({ dailyBudgetCa: e.target.value ? Number(e.target.value) : null })
              }
            />
          </label>
        </div>
        <AdCreativeForm value={form} onChange={(patch) => set(patch)} />

        {/* Nhắm mục tiêu + ước tính tiếp cận */}
        <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold ui-text">Nhắm mục tiêu</span>
            {reach ? (
              <span className="inline-flex items-center gap-1 text-xs text-[#0071A6] font-medium">
                <Users size={13} />
                Ước tính ~{reach.estimatedReach.toLocaleString('vi-VN')} người
              </span>
            ) : null}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-xs ui-muted">
              Giới tính
              <select
                className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none ui-text"
                value={form.targetGender || ''}
                onChange={(e) => set({ targetGender: e.target.value || null })}
              >
                {GENDERS.map((g) => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))}
              </select>
            </label>
            <label className="text-xs ui-muted">
              Thiết bị
              <select
                className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none ui-text"
                value={form.targetDevice || ''}
                onChange={(e) => set({ targetDevice: e.target.value || null })}
              >
                {DEVICES.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs ui-muted">
                Tuổi từ
                <input
                  type="number"
                  className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none ui-text"
                  value={form.targetAgeMin ?? ''}
                  onChange={(e) => set({ targetAgeMin: e.target.value ? Number(e.target.value) : null })}
                  placeholder="13"
                />
              </label>
              <label className="text-xs ui-muted">
                đến
                <input
                  type="number"
                  className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none ui-text"
                  value={form.targetAgeMax ?? ''}
                  onChange={(e) => set({ targetAgeMax: e.target.value ? Number(e.target.value) : null })}
                  placeholder="65"
                />
              </label>
            </div>
            <label className="text-xs ui-muted">
              Tỉnh/Thành (để trống = toàn quốc)
              <select
                className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none ui-text"
                value={form.targetCity || ''}
                onChange={(e) => set({ targetCity: e.target.value || null })}
              >
                <option value="">Toàn quốc</option>
                {VN_PROVINCES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </label>
          </div>

          {/* Sở thích / mối quan tâm — đánh đúng tệp */}
          <div>
            <div className="text-xs ui-muted mb-1">Sở thích/mối quan tâm (để trống = mọi người)</div>
            <div className="flex flex-wrap gap-2">
              {INTERESTS.map((it) => {
                const on = (form.targetInterests || []).includes(it);
                return (
                  <button
                    type="button"
                    key={it}
                    onClick={() =>
                      set({
                        targetInterests: on
                          ? (form.targetInterests || []).filter((x) => x !== it)
                          : [...(form.targetInterests || []), it],
                      })
                    }
                    className={`text-xs rounded-full px-3 py-1.5 border ${on ? 'bg-[#0071A6] text-white border-[#0071A6]' : 'ui-muted ui-divider'}`}
                  >
                    {it}
                  </button>
                );
              })}
            </div>
          </div>

          <CreatorDistributionSection
            distribution={form.creatorDistribution || 'Auto'}
            creators={pickedCreators}
            onChange={(d, creators) => {
              setPickedCreators(creators);
              set({
                creatorDistribution: d,
                targetCreatorIds: creators.map((c) => c.userId),
                // Named/Marketplace chỉ chạy trên bề mặt creator: bỏ Feed khỏi vị trí đã chọn.
                ...(d !== 'Auto'
                  ? { targetPlacements: (form.targetPlacements || []).filter((x) => x !== 'Feed') }
                  : {}),
              });
            }}
          />

          {/* Vị trí hiển thị */}
          <div>
            <div className="text-xs ui-muted mb-1">Vị trí hiển thị (để trống = mọi vị trí)</div>
            <div className="flex flex-wrap gap-2">
              {placementsForDistribution(form.creatorDistribution).map(({ value: pl, label: plLabel }) => {
                const on = (form.targetPlacements || []).includes(pl);
                return (
                  <button
                    type="button"
                    key={pl}
                    onClick={() =>
                      set({
                        targetPlacements: on
                          ? (form.targetPlacements || []).filter((x) => x !== pl)
                          : [...(form.targetPlacements || []), pl],
                      })
                    }
                    className={`text-xs rounded-full px-3 py-1.5 border ${on ? 'bg-[#0071A6] text-white border-[#0071A6]' : 'ui-muted ui-divider'}`}
                  >
                    {plLabel}
                  </button>
                );
              })}
            </div>
          </div>

          {reach ? (
            <p className="text-[11px] ui-muted">
              Trên tổng ~{reach.audienceBase.toLocaleString('vi-VN')} người có thể xem quảng cáo (không tính Cab+ Pro).
              Con số mang tính ước tính.
            </p>
          ) : null}
        </div>

        {/* Xem trước quảng cáo */}
        <div>
          <div className="text-sm font-semibold ui-text mb-1">
            Xem trước — mục tiêu: {objectiveLabel}
          </div>
          <AdPreview
            title={form.title}
            body={form.body}
            imageUrl={form.imageUrl}
            videoUrl={form.videoUrl || undefined}
            ctaText={form.ctaText}
            format={form.format}
          />
        </div>

        <ButtonPrimary
          onClick={submit}
          disabled={submitting}
          className="w-full justify-center h-10 bg-[#0071A6]"
        >
          {submitting
            ? 'Đang tạo…'
            : fundingAcc
            ? 'Tạo & trừ ngân sách từ ví DN'
            : 'Tạo & nạp ngân sách từ Ca nạp'}
        </ButtonPrimary>
      </div>

      {/* Danh sách chiến dịch */}
      <div className="space-y-2">
        <h2 className="font-semibold ui-text">Chiến dịch của tôi</h2>
        {loading ? (
          <p className="ui-muted text-sm">Đang tải…</p>
        ) : campaigns.length === 0 ? (
          <p className="ui-muted text-sm">Chưa có chiến dịch nào.</p>
        ) : (
          campaigns.map((c) => {
            const st = STATUS_VI[c.status] || { label: c.status, cls: 'bg-slate-200 text-slate-700' };
            return (
              <div key={c.id} className="rounded-xl ui-divider border p-3 flex items-center gap-3">
                {c.creative?.imageUrl ? (
                  <img src={c.creative.imageUrl} alt="" className="w-14 h-14 rounded-md object-cover shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-md bg-slate-100 dark:bg-slate-700 grid place-items-center shrink-0">
                    <Megaphone size={18} className="ui-muted" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium ui-text truncate">{c.name}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                    {c.fatigueDetectedAt && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                        Đang nhàm
                      </span>
                    )}
                    {c.adAccountId && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                        Ví DN{c.businessName ? `: ${c.businessName}` : ''}
                      </span>
                    )}
                  </div>
                  <div className="text-xs ui-muted">
                    {c.format} · {c.pricingModel} · thầu {c.bidCa} Ca ·{' '}
                    đã tiêu {c.spentCa}/{c.totalBudgetCa} Ca · còn {c.remainingCa} Ca
                  </div>
                  {c.creatorDistribution && (
                    <div className="text-xs ui-muted">
                      {c.creatorDistribution === 'Auto'
                        ? 'Phân phối: tự động'
                        : c.creatorDistribution === 'Named'
                        ? `Đích danh: ${(c.targetCreators || []).map((t) => t.fullName).join(', ') || '—'}`
                        : `Đăng lên sàn · ${c.optInCount ?? 0} creator đã chọn`}
                    </div>
                  )}
                  <div className="text-xs ui-muted">
                    {c.impressions} hiển thị · {c.clicks} click · CTR {c.ctr ?? 0}%
                    {c.status === 'Rejected' && c.rejectReason ? ` · Lý do: ${c.rejectReason}` : ''}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button
                    onClick={() => setStatsCampaign(c)}
                    className="rounded-md ui-divider border px-3 py-1.5 text-sm ui-text hover:bg-black/5 dark:hover:bg-white/5 flex items-center gap-1"
                  >
                    <BarChart3 size={14} /> Báo cáo
                  </button>
                  {(c.status === 'Active' || c.status === 'Paused') && (
                    <button
                      onClick={() => togglePause(c)}
                      className="rounded-md ui-divider border px-3 py-1.5 text-sm ui-text hover:bg-black/5 dark:hover:bg-white/5 flex items-center gap-1"
                    >
                      {c.status === 'Paused' ? <Play size={14} /> : <Pause size={14} />}
                      {c.status === 'Paused' ? 'Chạy lại' : 'Tạm dừng'}
                    </button>
                  )}
                  {(c.status === 'Active' || c.status === 'Paused' || c.status === 'Pending') && (
                    <button
                      onClick={() => cancelCampaign(c)}
                      title="Dừng hẳn & hoàn phần ngân sách chưa tiêu"
                      className="rounded-md border border-rose-200 dark:border-rose-900/40 px-3 py-1.5 text-sm text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 flex items-center gap-1"
                    >
                      <X size={14} /> Dừng & hoàn
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {statsCampaign && (
        <AdStatsModal
          campaign={statsCampaign}
          onClose={() => setStatsCampaign(null)}
          onChanged={load}
        />
      )}
      {creditGate.modal}
      {personalGate.modal}
    </div>
  );
};

export default AdsPage;
